import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import MemberHighlightsPageView from './MemberHighlightsPageView';
import { MemberOwnedNoteSnapshotsPage } from './MemberOwnedNoteSnapshotsPage';

import type {
  MemberHighlightsPageStatus,
  MemberHighlightsPageViewProps,
} from './MemberHighlightsPageView';
import type {
  MomentsSavedHighlight,
  MomentsSavedHighlightSort,
} from '@/store/api/momentsApi';

import {
  getSessionReflectionPath,
  getSessionReflectionSortTime,
} from '@/components/Home/SessionReflections/sessionReflectionHomeUtils';
import {
  isMemberReflectionProfile,
  MEMBER_REFLECTION_PROFILE_SLUGS,
} from '@/components/Sessions/memberReflectionProfiles';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useReflectionKeepFlag } from '@/hooks/useReflectionKeepCohort';
import {
  buildMemberMomentsAnalyticsProperties,
  MEMBER_MOMENTS_EVENTS,
  momentsAnalytics,
} from '@/services/momentsAnalytics';
import {
  useGetMomentsMemberArtifactsQuery,
  useSearchMomentsSavedHighlightsQuery,
} from '@/store/api/momentsApi';

const PAGE_SIZE = 20;

const mergeHighlights = (
  current: MomentsSavedHighlight[],
  incoming: MomentsSavedHighlight[],
): MomentsSavedHighlight[] => {
  if (current.length === 0) {
    return incoming;
  }
  const seen = new Set(current.map((highlight) => highlight.id));
  return [
    ...current,
    ...incoming.filter((highlight) => !seen.has(highlight.id)),
  ];
};

const getStatus = ({
  hasSearch,
  highlights,
  isError,
  isFirstPageLoading,
}: {
  hasSearch: boolean;
  highlights: MomentsSavedHighlight[];
  isError: boolean;
  isFirstPageLoading: boolean;
}): MemberHighlightsPageStatus => {
  if (isFirstPageLoading) {
    return 'loading';
  }
  if (isError && highlights.length === 0) {
    return 'error';
  }
  if (hasSearch && highlights.length === 0) {
    return 'search-empty';
  }
  return 'ready';
};

const SourceVisibleHighlightsPage = () => {
  const handleBack = useBackNavigation();
  const showManageSavedNotes = useReflectionKeepFlag();
  const { user } = useCurrentUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState<MomentsSavedHighlightSort>('created_at');
  const [offset, setOffset] = useState(0);
  const [highlights, setHighlights] = useState<MomentsSavedHighlight[]>([]);
  const [hasShownControls, setHasShownControls] = useState(false);
  const trimmedSearchTerm = searchTerm.trim();

  const searchArg = {
    direction: 'desc' as const,
    limit: PAGE_SIZE,
    offset,
    profileSlug: MEMBER_REFLECTION_PROFILE_SLUGS,
    q: trimmedSearchTerm || undefined,
    sort,
  };
  const {
    data: highlightsData,
    isError: isHighlightsError,
    isFetching: isHighlightsFetching,
    isLoading: isHighlightsLoading,
  } = useSearchMomentsSavedHighlightsQuery(searchArg);

  useEffect(() => {
    if (!highlightsData) {
      return;
    }
    if (highlightsData.totalCount > 0 || highlightsData.items.length > 0) {
      setHasShownControls(true);
    }
    setHighlights((current) =>
      offset === 0
        ? highlightsData.items
        : mergeHighlights(current, highlightsData.items),
    );
  }, [highlightsData, offset]);

  const latestReflectionArg = {
    includeContent: false,
    limit: 5,
    offset: 0,
    profileSlug: MEMBER_REFLECTION_PROFILE_SLUGS,
  };
  const { data: latestReflectionData } =
    useGetMomentsMemberArtifactsQuery(latestReflectionArg);
  const emptyHighlightHref = useMemo(() => {
    const latest = [...(latestReflectionData?.items ?? [])]
      .filter((artifact) => isMemberReflectionProfile(artifact.profileSlug))
      .sort(
        (a, b) =>
          getSessionReflectionSortTime(b) - getSessionReflectionSortTime(a),
      )[0];
    return latest ? getSessionReflectionPath(latest) : undefined;
  }, [latestReflectionData?.items]);

  const displayedHighlights =
    offset === 0 && highlightsData ? highlightsData.items : highlights;
  const hasSearch = trimmedSearchTerm.length > 0;
  const isLoadingMore = isHighlightsFetching && offset > 0;
  const isFirstPageLoading =
    isHighlightsLoading ||
    (isHighlightsFetching && offset === 0 && displayedHighlights.length === 0);
  const status = getStatus({
    hasSearch,
    highlights: displayedHighlights,
    isError: isHighlightsError,
    isFirstPageLoading,
  });
  useEffect(() => {
    if (status !== 'ready') {
      return;
    }
    momentsAnalytics.viewed(
      MEMBER_MOMENTS_EVENTS.viewedHighlights,
      'member_highlights',
      window.location.pathname,
      buildMemberMomentsAnalyticsProperties({
        entrypoint: 'direct',
        highlightCount: displayedHighlights.length,
        route: window.location.pathname,
        surface: 'highlights',
      }),
      { dedupeKey: 'highlights-page' },
    );
  }, [displayedHighlights.length, status]);

  const hasMore = highlightsData?.hasMore === true || isLoadingMore;

  const resetPagination = useCallback(() => {
    setOffset(0);
    setHighlights([]);
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      setHasShownControls(true);
      momentsAnalytics.track(
        MEMBER_MOMENTS_EVENTS.searchedHighlights,
        buildMemberMomentsAnalyticsProperties({
          queryLength: value.trim().length,
          route: window.location.pathname,
          surface: 'highlights',
        }),
      );
      resetPagination();
    },
    [resetPagination],
  );

  const handleSortChange = useCallback(
    (value: MomentsSavedHighlightSort) => {
      setSort(value);
      setHasShownControls(true);
      momentsAnalytics.track(
        MEMBER_MOMENTS_EVENTS.sortedHighlights,
        buildMemberMomentsAnalyticsProperties({
          route: window.location.pathname,
          sort: value,
          surface: 'highlights',
        }),
      );
      resetPagination();
    },
    [resetPagination],
  );

  const handleLoadMore = useCallback(() => {
    if (isHighlightsFetching || !hasMore || !highlightsData) {
      return;
    }
    setOffset(highlightsData.nextOffset ?? highlights.length);
  }, [hasMore, highlights.length, highlightsData, isHighlightsFetching]);

  const member: MemberHighlightsPageViewProps['member'] = {
    avatarUrl: user.avatarUrl,
    firstName: user.firstName,
    lastName: user.lastName,
    name:
      user.fullName ||
      [user.firstName, user.lastName].filter(Boolean).join(' ') ||
      undefined,
  };

  return (
    <MemberHighlightsPageView
      emptyHighlightHref={emptyHighlightHref}
      hasMore={hasMore}
      highlights={displayedHighlights}
      isLoadingMore={isLoadingMore}
      isRefreshing={
        isHighlightsFetching && offset === 0 && displayedHighlights.length > 0
      }
      member={member}
      searchTerm={searchTerm}
      showControls={
        hasShownControls || displayedHighlights.length > 0 || hasSearch
      }
      showManageSavedNotes={showManageSavedNotes}
      sort={sort}
      status={status}
      onBack={handleBack}
      onLoadMore={handleLoadMore}
      onSearchChange={handleSearchChange}
      onSortChange={handleSortChange}
    />
  );
};

const MemberHighlightsPage = () => {
  const [params] = useSearchParams();
  return params.get('view') === 'owned-snapshots' ? (
    <MemberOwnedNoteSnapshotsPage />
  ) : (
    <SourceVisibleHighlightsPage />
  );
};

export default MemberHighlightsPage;
